import React, { useContext } from "react";
import { SettingsContext } from "./SettingsContext.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import { ALGEBRA_MAX_VALUE_OPTIONS, AlgebraQuestionOptions } from "../Constants.ts";
import { withLabels } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * The two algebra dials (ADR 0028 §4).
 *
 * There is no "which operations" control here on purpose: algebra reads the
 * operator bar, so a multiplication-only sheet is all `6a = 42` without any
 * setting of its own. The largest value is the difficulty dial teachers
 * actually reach for, in the same shape as the money and denominator limits.
 */
export function AlgebraSettings() {
    const { settings, setSettings } = useContext(SettingsContext);
    const t = useTranslation();

    function handleQuestionChange(value: AlgebraQuestionOptions) {
        // SelectButton hands back null when the selected option is clicked
        // again; a sheet has to ask something.
        if (!value) {
            return;
        }
        setSettings({ ...settings, AlgebraQuestion: value });
    }

    function handleMaxValueChange(event) {
        setSettings({ ...settings, AlgebraMaxValue: Number(event.target.value) });
    }

    return (
        <div className="algebraSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={settings.AlgebraQuestion}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(AlgebraQuestionOptions), settings.Language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>
            <div className="selectContainer">
                <span className="settingLabel">{t('settings.largestNumberLabel')}</span>
            </div>
            <div className="radioButtons" role="radiogroup" aria-label={t('settings.largestNumberAria')}>
                {ALGEBRA_MAX_VALUE_OPTIONS.map((option) => (
                    <div key={option}>
                        <RadioButton
                            inputId={`algebraMaxValue-${option}`}
                            value={option}
                            checked={settings.AlgebraMaxValue === option}
                            onChange={handleMaxValueChange}
                        />
                        <label htmlFor={`algebraMaxValue-${option}`}>{option}</label>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default AlgebraSettings;
